import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { cn } from "@/lib/utils";
import { Kicker } from "@/components/ui/kicker";
import { PersonaVoice } from "@/components/ui/persona-voice";
import { FindingRow } from "@/components/experience/finding-row";
import { StepScreenshot, type TargetBox, isValidBox } from "./step-screenshot";
import { TimelineZoomDialog } from "./timeline-zoom-dialog";
import {
  CitationText,
  type ResolvedCitationData,
} from "@/components/citations";
import type { FindingType, Severity } from "@/components/finding-badges";
import { MilestoneTimeline } from "./milestone-timeline";
import { buildMilestones, type Milestone } from "@/lib/milestones";

export interface TimelineStep {
  id: string;
  stepNumber: number;
  title: string;
  personaReasoning?: string | null;
  grounding?: string | null;
  screenshotPath?: string | null;
  pageUrl?: string | null;
  surface?: string | null;
  routeKey?: string | null;
}

export interface TimelineFinding {
  id: string;
  type: string;
  severity: string | null;
  title: string | null;
  description: string;
  walkthroughStepId?: string | null;
  targetBox?: TargetBox | null;
}

export function StepsTimeline({
  steps,
  findings,
  walkthroughId,
  personaName,
  citationsMap,
  openPanel,
  projectId,
  onSelectFinding,
  highlightedStepNumber,
  onOpenZenMode,
}: {
  steps: TimelineStep[];
  findings: TimelineFinding[];
  walkthroughId: string;
  personaName?: string | null;
  citationsMap?: Map<string, ResolvedCitationData>;
  openPanel?: (sourceType: string, sourceId: string) => void;
  projectId?: string;
  onSelectFinding?: (findingId: string) => void;
  highlightedStepNumber?: number | null;
  onOpenZenMode?: (findingIndex: number) => void;
}) {
  const stepsWithScreenshots = useMemo(
    () => steps.filter((s) => !!s.screenshotPath),
    [steps],
  );

  const [zoomedStepNumber, setZoomedStepNumber] = useState<number | null>(null);
  // The control that opened the zoom dialog. It has no DialogTrigger, so
  // Radix cannot return focus on close by itself.
  const zoomOpenerRef = useRef<HTMLElement | null>(null);
  const openZoom = useCallback((stepNumber: number) => {
    zoomOpenerRef.current =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;
    setZoomedStepNumber(stepNumber);
  }, []);

  const stepIdToNumber = useMemo(
    () => new Map(steps.map((s) => [s.id, s.stepNumber])),
    [steps],
  );

  const findingsByStepNumber = useMemo(() => {
    const map = new Map<number, TimelineFinding[]>();
    for (const f of findings) {
      if (!f.walkthroughStepId) continue;
      const stepNum = stepIdToNumber.get(f.walkthroughStepId);
      if (typeof stepNum === "number") {
        const arr = map.get(stepNum) ?? [];
        arr.push(f);
        map.set(stepNum, arr);
      }
    }
    return map;
  }, [findings, stepIdToNumber]);

  const [hoveredFindingId, setHoveredFindingId] = useState<string | null>(null);

  useEffect(() => {
    if (highlightedStepNumber === null || highlightedStepNumber === undefined)
      return;
    const el = document.getElementById(`step-${highlightedStepNumber}`);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [highlightedStepNumber]);

  const milestones = useMemo(() => buildMilestones(steps), [steps]);
  const [selectedMilestoneId, setSelectedMilestoneId] = useState<string | null>(
    null,
  );

  const visibleSteps = useMemo(() => {
    if (!selectedMilestoneId) return steps;
    const m = milestones.find((x) => x.id === selectedMilestoneId);
    if (!m) return steps;
    return steps.filter((s) => m.stepNumbers.includes(s.stepNumber));
  }, [steps, milestones, selectedMilestoneId]);

  const handleSelectMilestone = useCallback((m: Milestone) => {
    setSelectedMilestoneId((prev) => (prev === m.id ? null : m.id));
    const el = document.getElementById(`step-${m.startStepNumber}`);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, []);

  if (steps.length === 0) return null;

  return (
    <section className="space-y-4" aria-labelledby="timeline-steps">
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <Kicker as="h2" className="text-foreground">
          <span id="timeline-steps">Execution trace</span>
        </Kicker>
        {personaName && (
          <span className="text-sm text-muted-foreground">
            What {personaName.split(" ")[0]} did, in order ({steps.length}{" "}
            steps)
          </span>
        )}
      </div>

      {milestones.length > 0 && (
        <MilestoneTimeline
          milestones={milestones}
          activeStepNumber={highlightedStepNumber}
          selectedMilestoneId={selectedMilestoneId}
          onSelectMilestone={handleSelectMilestone}
          onClearFilter={() => setSelectedMilestoneId(null)}
        />
      )}

      <ol className="space-y-6">
        {visibleSteps.map((step) => {
          const stepFindings = findingsByStepNumber.get(step.stepNumber) ?? [];
          const primaryTargetBox =
            stepFindings.find((f) => f.targetBox)?.targetBox ?? null;
          const isHighlighted = highlightedStepNumber === step.stepNumber;

          return (
            <li
              key={step.id}
              id={`step-${step.stepNumber}`}
              aria-current={isHighlighted ? "step" : undefined}
              className={cn(
                "rounded-lg border bg-card p-4 transition-all scroll-mt-20",
                isHighlighted
                  ? "border-primary ring-2 ring-primary/40 bg-accent/20 shadow-sm"
                  : "border-border hover:border-border/80",
              )}
            >
              <div className="grid gap-6 lg:grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)]">
                {/* Step narrative and notes */}
                <div className="space-y-3">
                  <div className="flex items-baseline gap-3">
                    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-muted text-xs font-semibold tabular-nums text-foreground">
                      {step.stepNumber}
                    </span>
                    <h3 className="text-base font-semibold text-foreground">
                      {step.title}
                    </h3>
                  </div>

                  {step.personaReasoning && (
                    <PersonaVoice size="base" className="pl-9 leading-relaxed">
                      {citationsMap && openPanel ? (
                        <CitationText
                          text={step.personaReasoning}
                          citations={citationsMap}
                          onOpenPanel={openPanel}
                        />
                      ) : (
                        step.personaReasoning
                      )}
                    </PersonaVoice>
                  )}

                  {step.grounding && citationsMap && openPanel && (
                    <div className="pl-9 text-sm text-muted-foreground">
                      <CitationText
                        text={step.grounding}
                        citations={citationsMap}
                        onOpenPanel={openPanel}
                      />
                    </div>
                  )}

                  {/* Findings discovered on this step */}
                  {stepFindings.length > 0 && (
                    <div className="space-y-2 pl-9 pt-2">
                      <span className="text-xs font-semibold text-muted-foreground">
                        Findings observed at this step ({stepFindings.length})
                      </span>
                      <div className="flex flex-col gap-1.5">
                        {stepFindings.map((f, i) => {
                          const isTargeted = isValidBox(f.targetBox);
                          const targetIndex = stepFindings
                            .slice(0, i + 1)
                            .filter((item) =>
                              isValidBox(item.targetBox),
                            ).length;

                          return (
                            <div
                              key={f.id}
                              onMouseEnter={() =>
                                isTargeted && setHoveredFindingId(f.id)
                              }
                              onMouseLeave={() =>
                                isTargeted && setHoveredFindingId(null)
                              }
                            >
                              <FindingRow
                                variant="pill"
                                href={
                                  projectId
                                    ? `/projects/${projectId}/findings/${f.id}`
                                    : undefined
                                }
                                onSelect={
                                  onSelectFinding
                                    ? () => onSelectFinding(f.id)
                                    : undefined
                                }
                                finding={{
                                  id: f.id,
                                  title: f.title,
                                  description: f.description,
                                  type: f.type as FindingType,
                                  severity: f.severity as Severity,
                                }}
                                badge={
                                  isTargeted ? (
                                    <span
                                      className={cn(
                                        "shrink-0 rounded px-1.5 py-0.5 text-xs font-semibold transition-colors",
                                        f.type === "helps"
                                          ? hoveredFindingId === f.id
                                            ? "bg-emerald-600 text-white ring-1 ring-emerald-600"
                                            : "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400"
                                          : hoveredFindingId === f.id
                                            ? "bg-destructive text-destructive-foreground ring-1 ring-destructive"
                                            : "bg-destructive/15 text-destructive",
                                      )}
                                    >
                                      #{targetIndex} Target
                                    </span>
                                  ) : undefined
                                }
                              />
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>

                {/* Screenshot view */}
                {step.screenshotPath && (
                  <div className="overflow-hidden rounded-md border border-border/70 bg-muted/20">
                    <StepScreenshot
                      walkthroughId={walkthroughId}
                      stepNumber={step.stepNumber}
                      alt={`Step ${step.stepNumber} screenshot`}
                      highlights={stepFindings
                        .filter((f) => isValidBox(f.targetBox))
                        .map((f, i) => ({
                          id: f.id,
                          box: f.targetBox!,
                          tone: (f.severity || f.type) as string,
                          label: f.title || f.description,
                          index: i + 1,
                          isActive: f.id === hoveredFindingId,
                          isDimmed: Boolean(
                            hoveredFindingId && f.id !== hoveredFindingId,
                          ),
                        }))}
                      activeHighlightId={hoveredFindingId}
                      onSelectHighlight={onSelectFinding}
                      onOpenZoom={(stepNumber) => {
                        const stepObj = steps.find(
                          (s) => s.stepNumber === stepNumber,
                        );
                        const fIdx = findings.findIndex(
                          (f) => f.walkthroughStepId === stepObj?.id,
                        );
                        if (onOpenZenMode && fIdx >= 0) {
                          onOpenZenMode(fIdx);
                        } else {
                          openZoom(stepNumber);
                        }
                      }}
                    />
                  </div>
                )}
              </div>
            </li>
          );
        })}
      </ol>

      {/* Screenshot Zoom Gallery Dialog */}
      <TimelineZoomDialog
        open={zoomedStepNumber !== null}
        onClose={() => setZoomedStepNumber(null)}
        stepNumber={zoomedStepNumber}
        stepsWithScreenshots={stepsWithScreenshots}
        totalSteps={steps.length}
        findingsByStepNumber={findingsByStepNumber}
        walkthroughId={walkthroughId}
        onSelectFinding={onSelectFinding}
        openerElement={zoomOpenerRef.current}
      />
    </section>
  );
}
